import React, { useEffect, useState } from 'react';

import { queryClient } from '@/data';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { useAuthUserSettings, useUpdateUserSettings } from '@/hooks/use-auth-user-settings';
import { logger } from '@/utils/logging';

import { CustomSwitch } from './CustomSwitch';

type ProjectionChangeSwitchProps = {
    displayMode: 'modal' | 'preferences';
    onSwitchChange?: (value: boolean) => void;
};

const PROJECTION_CHANGE_SWITCH = '[projection change switch]: ';
export const ProjectionChangeSwitch = ({ displayMode, onSwitchChange }: ProjectionChangeSwitchProps) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const { data } = useAuthUserSettings();
    const { mutateAsync: updateUserSettings } = useUpdateUserSettings();
    const acceptAllProjectionsChanges = data && data.accept_all_odds_changes;

    useEffect(() => {
        if (acceptAllProjectionsChanges) {
            setIsEnabled(acceptAllProjectionsChanges);
        }
    }, [acceptAllProjectionsChanges]);

    const handleProjectionChangeToggle = async () => {
        const newEnabledState = !isEnabled;
        if (displayMode === 'modal' && newEnabledState) {
            BetrAnalytics.trackEvent(AnalyticsEvent.ALWAYS_ACCEPT_PROJECTION_CHANGES);
        }
        if (onSwitchChange) {
            onSwitchChange(newEnabledState);
            setIsEnabled(newEnabledState);
        } else {
            setIsEnabled(newEnabledState);
            try {
                await updateUserSettings({ accept_all_odds_changes: newEnabledState });
                await queryClient.invalidateQueries({ queryKey: authUserKeys.settings() });
            } catch (error: unknown) {
                setIsEnabled(!newEnabledState);
                logger.error(PROJECTION_CHANGE_SWITCH, 'Error updating user settings', error);
            }
        }
    };

    return <CustomSwitch val={isEnabled} handleOnPress={handleProjectionChangeToggle} />;
};
